import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert';
import 'bootstrap/dist/css/bootstrap.min.css';

// Ensure this path matches your project structure
import * as Constants from "./Constants/index";

// --- STYLES ---
const styles = {
    body: {
        backgroundColor: '#f6f8f7',
        fontFamily: '"Segoe UI", Inter, Arial, sans-serif',
        minHeight: '100vh',
        color: '#1f2937',
    },
    topbar: {
        height: '72px',
        backgroundColor: '#4f8f1f',
        display: 'flex',
        alignItems: 'center',
        padding: '0 40px',
    },
    logo: {
        height: '42px',
    },
    wrapper: {
        maxWidth: '1200px',
        margin: '50px auto',
    },
    loginCard: {
        backgroundColor: '#fff',
        borderRadius: '18px',
        boxShadow: '0 20px 40px rgba(0,0,0,.1)',
        padding: '38px 34px',
        width: '100%',
        maxWidth: '420px',
        margin: '0 auto',
    },
    heading: {
        margin: '0',
        fontSize: '26px',
        fontWeight: 'bold',
    },
    subtext: {
        margin: '8px 0 26px',
        fontSize: '14px',
        color: '#6b7280',
    },
    label: {
        fontSize: '13px',
        marginBottom: '6px',
        display: 'block',
        fontWeight: '500',
    },
    input: {
        width: '100%',
        padding: '14px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
        marginBottom: '10px',
        fontSize: '15px',
    },
    button: {
        width: '100%',
        backgroundColor: '#4f8f1f',
        color: '#fff',
        border: 'none',
        padding: '14px',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        marginTop: '10px'
    },
    otpContainer: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'space-between',
        marginBottom: '20px',
    },
    otpInput: {
        width: '100%',
        height: '52px',
        textAlign: 'center',
        fontSize: '18px',
        borderRadius: '10px',
        border: '1px solid #d1d5db',
    },
    note: {
        marginTop: '14px',
        fontSize: '12px',
        color: '#6b7280',
        textAlign: 'center',
    },
    errorText: {
        color: '#dc3545',
        fontSize: '12px',
        marginBottom: '15px',
        marginTop: '-5px'
    },
    visualContainer: {
        // Flex column to stack Image + Description
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-center', // Aligns right
    },
    visualImg: {
        width: '100%',
        maxWidth: '520px', // Matches your HTML
        borderRadius: '20px',
        boxShadow: '0 30px 60px rgba(0,0,0,.15)',
        // Animation is applied here, keyframes are defined in the return block
        animation: 'float 5s ease-in-out infinite',
    },
    descriptionBox: {
        marginTop: '24px',
        maxWidth: '520px',
        backgroundColor: '#ffffff',
        padding: '20px 22px',
        borderRadius: '16px',
        boxShadow: '0 12px 28px rgba(0, 0, 0, .08)',
        textAlign: "start"
    },
    descriptionTitle: {
        margin: '0 0 8px',
        fontSize: '16px',
        color: '#2f6b0f',
        fontWeight: 'bold'
    },
    descriptionText: {
        margin: '0',
        fontSize: '14px',
        color: '#4b5563',
        lineHeight: '1.6',
    },
    footer: {
        textAlign: 'center',
        margin: '40px 0',
        fontSize: '14px',
    },
    footerLink: {
        color: '#4f8f1f',
        margin: '0 12px',
        textDecoration: 'none',
    },
    resendLink: {
        display: 'block',
        textAlign: 'center',
        marginTop: '15px',
        color: '#4f8f1f',
        textDecoration: 'none',
        fontSize: '14px',
        cursor: 'pointer'
    },
};

const LoginWithOtp = () => {
    const navigate = useNavigate();

    // --- STATE ---
    const [step, setStep] = useState('MOBILE');
    const [mobile, setMobile] = useState('');
    const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const otpInputsRef = useRef([]);

    // --- VALIDATION ---
    const validateForm = () => {
        let newErrors = {};
        if (!mobile) newErrors.mobile = 'Phone Number is required';
        else if (!/^\d+$/.test(mobile)) newErrors.mobile = 'Number should be numeric';
        else if (mobile.length !== 10) newErrors.mobile = 'Mobile Number should be 10 digits';
        return newErrors;
    };

    const validateOtp = (otpString) => {
        let newErrors = {};
        if (!otpString) newErrors.otp = 'Please Enter OTP';
        else if (otpString.length !== 6) newErrors.otp = 'OTP must be 6 digits';
        return newErrors;
    };

    // --- API HANDLERS ---
    const handleLogin = async () => {
        setErrors({});
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            const res = await axios.post(Constants.baseUrl + 'auth/login-otp',
                { mobile: mobile, password: '', userType: 'SALES' },
                {
                    headers: {
                        'Accept-Language': localStorage.getItem('language'),
                        appVersion: Constants.av,
                        platform: Constants.pt,
                        company: Constants.company,
                        program: Constants.program,
                    },
                }
            );

            setLoading(false);

            if (res.status === 200) {
                if (res.data.code === 16001) {
                    swal({ text: res.data.msg, buttons: false, timer: 2000 });
                    setStep('OTP');
                } else {
                    swal({ text: res.data.msg, icon: 'error', buttons: false, timer: 2000 });
                }
            } else {
                swal({ text: res.data.msg, buttons: false, timer: 2000 });
            }
        } catch (error) {
            setLoading(false);
            swal({ text: "Network Error", icon: "error", timer: 2000, buttons: false });
        }
    };

    const verifyOtp = async () => {
        setErrors({});
        const otpString = otpArray.join("");
        const validationErrors = validateOtp(otpString);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        try {
            const res = await axios.post(Constants.baseUrl + 'auth/verify-otp/newSales',
                { mobile: mobile, otp: otpString, userType: 'SALES' },
                {
                    headers: {
                        'Accept-Language': localStorage.getItem('language'),
                        appVersion: Constants.av,
                        platform: Constants.pt,
                        company: Constants.company,
                        program: Constants.program,
                    },
                }
            );

            setLoading(false);

            if (res.status === 200) {
                if (res.data.code === 10001) {
                    localStorage.setItem('user_id', res.data.output.user.id);
                    localStorage.setItem('user_type', res.data.output.user.userType);
                    localStorage.setItem('user_name', res.data.output.user.name);
                    localStorage.setItem('user_email', res.data.output.user.email);
                    localStorage.setItem('user_mobile', res.data.output.user.mobile);
                    localStorage.setItem('token', res.data.output.access.token);

                    swal({ text: "Login Successful", icon: "success", buttons: false, timer: 1500 });
                    navigate('/sales_dashboard');
                } else {
                    swal({ text: res.data.msg || 'Login Failed', icon: 'error', buttons: false, timer: 2000 });
                }
            }
        } catch (error) {
            setLoading(false);
            swal({ text: "Verification Failed", icon: "error", timer: 2000, buttons: false });
        }
    };

    // --- UI HELPERS ---
    const handleMobileChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length <= 10) {
            setMobile(value);
            if (errors.mobile) setErrors({ ...errors, mobile: null });
        }
    };

    const handleOtpInput = (element, index) => {
        const value = element.value.replace(/[^0-9]/g, '');
        if (isNaN(value)) return;
        const newOtp = [...otpArray];
        newOtp[index] = value;
        setOtpArray(newOtp);
        if (errors.otp) setErrors({ ...errors, otp: null });
        if (value && index < 5) otpInputsRef.current[index + 1].focus();
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otpArray[index] && index > 0) {
            otpInputsRef.current[index - 1].focus();
        }
        if (e.key === 'Enter') verifyOtp();
    };

    const handleMobileKeyDown = (e) => {
        if (e.key === 'Enter') handleLogin();
    }

    return (
        <div style={styles.body}>
            {/* INJECT ANIMATION KEYFRAMES */}
            <style>
                {`
                    @keyframes float {
                        0% { transform: translateY(0px); }
                        50% { transform: translateY(-12px); }
                        100% { transform: translateY(0px); }
                    }
                    .fade-in {
                        animation: fadeIn 0.5s ease-in;
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>

            <div style={styles.topbar}>
                <img src={require('../assets/img/logo/newJkLogo.png')} alt="JK Seeds" style={styles.logo} />
            </div>

            <div className="container" style={styles.wrapper}>
                <div className="row align-items-center">

                    {/* LOGIN CARD */}
                    <div className="col-lg-5 col-md-12 mb-5 mb-lg-0">
                        <div style={styles.loginCard}>
                            {step === 'MOBILE' && (
                                <div className="fade-in">
                                    <h1 style={styles.heading}>Log In</h1>
                                    <p style={styles.subtext}>Secure access for authorized JK Seeds personnel</p>
                                    <label style={styles.label}>Phone Number</label>
                                    <input
                                        type="text"
                                        placeholder="Enter 10-digit mobile number"
                                        style={{ ...styles.input, borderColor: errors.mobile ? '#dc3545' : '#d1d5db' }}
                                        value={mobile}
                                        onChange={handleMobileChange}
                                        onKeyDown={handleMobileKeyDown}
                                    />
                                    {errors.mobile && <div style={styles.errorText}>{errors.mobile}</div>}
                                    <button
                                        onClick={handleLogin}
                                        disabled={loading}
                                        style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
                                    >
                                        {loading ? 'Sending...' : 'Send OTP'}
                                    </button>
                                </div>
                            )}

                            {step === 'OTP' && (
                                <div className="fade-in">
                                    <h1 style={styles.heading}>Verify OTP</h1>
                                    <p style={styles.subtext}>Enter the 6-digit OTP sent to {mobile}</p>
                                    <div style={styles.otpContainer}>
                                        {otpArray.map((data, index) => (
                                            <input
                                                key={index}
                                                type="text"
                                                maxLength="1"
                                                style={{ ...styles.otpInput, borderColor: errors.otp ? '#dc3545' : '#d1d5db' }}
                                                value={data}
                                                ref={(el) => (otpInputsRef.current[index] = el)}
                                                onChange={(e) => handleOtpInput(e.target, index)}
                                                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                            />
                                        ))}
                                    </div>
                                    {errors.otp && <div style={styles.errorText}>{errors.otp}</div>}
                                    <button
                                        onClick={verifyOtp}
                                        style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
                                        disabled={loading}
                                    >
                                        {loading ? 'Verifying...' : 'Verify & Login'}
                                    </button>
                                    <div style={styles.resendLink} onClick={handleLogin}>Resend OTP</div>
                                    <div
                                        style={{ ...styles.resendLink, color: '#6b7280', fontSize: '12px' }}
                                        onClick={() => {
                                            setStep('MOBILE');
                                            setOtpArray(new Array(6).fill(""));
                                            setErrors({});
                                        }}
                                    >
                                        Change Mobile Number
                                    </div>
                                </div>
                            )}
                            <div style={styles.note}>Restricted access · OTP-based authentication</div>
                        </div>
                    </div>

                    {/* RIGHT SIDE: IMAGE + DESCRIPTION */}
                    <div className="col-lg-7 d-none d-lg-block text-end">
                        <div style={styles.visualContainer}>
                            {/* FLOATING IMAGE */}
                            <img
                                src={require('../assets/img/logo/Webloginpage.jpeg')}
                                alt="JK Seeds Loyalty Program"
                                style={styles.visualImg}
                            />

                            {/* DESCRIPTION BOX */}
                            <div style={styles.descriptionBox}>
                                <h3 style={styles.descriptionTitle}>JK Seeds Retailer Loyalty Program</h3>
                                <p style={styles.descriptionText}>
                                    A dedicated initiative by JK Seeds to strengthen partnerships with
                                    retailers through transparent engagement, program visibility,
                                    and streamlined communication across the network.
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div style={styles.footer}>
                <Link to="#" style={styles.footerLink}>About JK Seeds</Link> |
                <Link to="#" style={styles.footerLink}>Privacy Policy</Link> |
                <Link to="#" style={styles.footerLink}>Contact IT Support</Link>
            </div>
        </div>
    );
};

export default LoginWithOtp;